'use client';

import { ChevronDown, ChevronUp, List } from 'lucide-react';
import { useEffect, useState } from 'react';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

/**
 * Auto-generated Table of Contents from H2/H3 tags inside the blog content.
 * Highlights the currently visible section as the user scrolls.
 * Mobile-friendly with collapsible toggle.
 */
export default function TableOfContents({ contentHtml }: { contentHtml: string }) {
  const [items, setItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Parse headings from rendered DOM after mount
  useEffect(() => {
    const container = document.querySelector('.blog-content');
    if (!container) return;

    const headings = container.querySelectorAll('h2, h3');
    const tocItems: TocItem[] = [];

    headings.forEach((el, i) => {
      const id = `toc-${i}`;
      el.setAttribute('id', id);
      tocItems.push({
        id,
        text: el.textContent?.trim() || '',
        level: el.tagName === 'H2' ? 2 : 3,
      });
    });

    setItems(tocItems);
  }, [contentHtml]);

  // Intersection observer to highlight active heading
  useEffect(() => {
    if (items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
    );

    items.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [items]);

  if (items.length < 2) return null;

  return (
    <nav className="w-full lg:w-[240px] lg:shrink-0 mb-4 lg:mb-0">
      {/* Mobile toggle button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden w-full flex items-center justify-between gap-2 px-4 py-3 bg-white rounded-xl border border-gray-200 shadow-sm mb-2"
      >
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <List size={16} />
          <span>İçindekiler</span>
        </div>
        {isMobileOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {/* TOC content */}
      <div
        className={`${
          isMobileOpen ? 'block' : 'hidden'
        } lg:block bg-white lg:bg-transparent rounded-xl lg:rounded-none border border-gray-200 lg:border-0 shadow-sm lg:shadow-none lg:sticky lg:top-44 lg:max-h-[calc(100vh-200px)] lg:overflow-y-auto`}
      >
        <div className="hidden lg:flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
          <List size={16} />
          <span>İçindekiler</span>
        </div>
        <ul className="flex flex-col gap-1 border-l-2 border-hzc_beige_400 px-4 py-3 lg:px-0 lg:py-0 max-h-[300px] lg:max-h-none overflow-y-auto lg:overflow-visible">
          {items.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById(item.id);
                  if (el) {
                    const offset = window.innerWidth >= 1024 ? 190 : 450;
                    const bodyRect = document.body.getBoundingClientRect().top;
                    const elementRect = el.getBoundingClientRect().top;
                    const elementPosition = elementRect - bodyRect;
                    const offsetPosition = elementPosition - offset;
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth',
                    });
                    // Close mobile TOC after clicking
                    setIsMobileOpen(false);
                  }
                }}
                className={`block text-sm py-1 transition-colors duration-200 ${
                  item.level === 3 ? 'pl-6' : 'pl-4'
                } ${
                  activeId === item.id
                    ? 'text-hzc_purple_500 font-medium border-l-2 border-hzc_purple_500 -ml-[2px]'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {item.text}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}
